import { AxiosResponse } from "axios";
import RequestUtilitario, { MutationBasicaParam, QueryBasicaParam } from "../../../Utils/RequisicaoUtilitario";
import { Movimentacao } from "../../../Models/Financeiro/Conta/Movimentacao";
import { Dayjs } from 'dayjs';
import { useMutation, useQuery } from "@tanstack/react-query";
import { Axios } from "../../Axios";
import { queryClient } from "../../../App";
import { IPagina, IPaginaInfo, PaginaInfo } from "@/Models/Utils/Pagina/Pagina";

export function useMovimentacaoDesativar({
    onSuccess,
    ...rest
}:MutationBasicaParam<Movimentacao, AxiosResponse<void>>) {
    return useMutation({
        ...rest,
        mutationFn: (movimentacao) => {
            return Axios.delete<void>(`financeiro/conta/movimentacao/${movimentacao.codigo}`);
        },
        onSuccess : (retorno, entrada, context) => {
            queryClient.invalidateQueries({ queryKey: ["Financeiro", "Conta", "Movimentacao"] });
            onSuccess?.(retorno, entrada, context);
        }
    });
}
export function useMovimentacaoSalvar({
    onSuccess,
    ...rest
}:MutationBasicaParam<Movimentacao, AxiosResponse<void>>) {
    return useMutation({
        ...rest,
        mutationFn: (movimentacao) => {
            movimentacao.codigo = Number.parseInt(movimentacao.codigo as any);
            const isAlteracao = !isNaN(movimentacao.codigo) && movimentacao.codigo > 0;
            return isAlteracao 
                ? Axios.put<void>(`financeiro/conta/movimentacao/${movimentacao.codigo}`, movimentacao) 
                : Axios.post<void>(`financeiro/conta/movimentacao`, movimentacao)
            ;
        },
        onSuccess : (retorno, entrada, context) => {
            queryClient.invalidateQueries({ queryKey: ["Financeiro", "Conta", "Movimentacao"] });
            onSuccess?.(retorno, entrada, context);
        }
    });
}

export function useMovimentacaoListar({
    movimentacao,
    ativo,
    entrada,
    dataInicial,
    dataFinal,
    onFinish,
    ...rest
}:QueryBasicaParam<AxiosResponse<Movimentacao[]>, Movimentacao[], {
    movimentacao?:Movimentacao|null,
    ativo?:boolean|null,
    entrada?:boolean|null,
    dataInicial?:Date|Dayjs|null,
    dataFinal?:Date|Dayjs|null,
    onFinish?:() => void
}>) {
    const queryString = RequestUtilitario.objectToQueryString({
        ...(movimentacao ?? {}),
        entrada,
        ativo,
        dataInicial,
        dataFinal
    });

    return useQuery({
        ...rest,
        queryKey : [ "Financeiro", "Conta", "Movimentacao", "Listar", queryString ],
        queryFn : () => Axios.get<Movimentacao[]>(`financeiro/conta/movimentacao?${queryString}`).finally(onFinish),
        select(data) {
            return data.data.map(m => {
                m.dataMovimentacao = new Date(m.dataMovimentacao as any);
                m.dataCriacao = new Date(m.dataCriacao as any);
                return m;
            });
        },
        staleTime : 1000 * 60 * 3
    });
}

export function useMovimentacaoListarPagina({
    pagina,
    movimentacao,
    entrada,
    ativo,
    dataInicial,
    dataFinal,
    onFinish,
    ...rest
}:QueryBasicaParam<AxiosResponse<IPagina<Movimentacao>>, IPagina<Movimentacao>, {
    pagina:IPaginaInfo,
    movimentacao?:Movimentacao|null,
    entrada?:boolean|null,
    ativo?:boolean|null,
    dataInicial?:Date|Dayjs|null,
    dataFinal?:Date|Dayjs|null,
    onFinish?:() => void
}>) {
    const queryString = RequestUtilitario.objectToQueryString({
        ...(pagina ?? new PaginaInfo()),
        ...(movimentacao ?? {}),
        ativo,
        entrada,
        dataInicial,
        dataFinal
    });

    return useQuery({
        ...rest,
        queryKey : [ "Financeiro", "Conta", "Movimentacao", "Listar", "Pagina", queryString ],
        queryFn : () => Axios.get<IPagina<Movimentacao>>(`financeiro/conta/movimentacao/pagina?${queryString}`).finally(onFinish),
        select(data) {
            const pagina = data.data;

            pagina.registros = pagina.registros.map(m => {
                m.dataMovimentacao = new Date(m.dataMovimentacao as any);
                m.dataCriacao = new Date(m.dataCriacao as any);
                return m;
            });

            return pagina;
        },
        staleTime : 1000 * 60 * 0
    });
}

export function useMovimentacaoTituloListar({
    pagina,
    codTitulo,
    onFinish,
    ...rest
}:QueryBasicaParam<AxiosResponse<IPagina<Movimentacao>>, IPagina<Movimentacao>, {
    pagina?:IPaginaInfo,
    codTitulo:number,
    onFinish?:() => void
}>) {
    const queryString = RequestUtilitario.objectToQueryString({
        ...(pagina ?? new PaginaInfo())
    });

    return useQuery({
        ...rest,
        queryKey : [ "Financeiro", "Conta", "Movimentacao", "Titulo", codTitulo, "Listar", "Pagina", queryString ],
        queryFn : () => Axios.get<IPagina<Movimentacao>>(`financeiro/conta/movimentacao/titulo/${codTitulo}?${queryString}`).finally(onFinish),
        select(data) {
            const pagina = data.data;

            pagina.registros = pagina.registros.map(m => {
                m.dataMovimentacao = new Date(m.dataMovimentacao as any);
                m.dataCriacao = new Date(m.dataCriacao as any);
                return m;
            });

            return pagina;
        },
        staleTime : 1000 * 60 * 0.15
    });
}

export function useMovimentacaoBuscar({
    codigo,
    onFinish,
    ...rest
}:QueryBasicaParam<AxiosResponse<Movimentacao|null>, Movimentacao|null, {
    codigo:number;
    onFinish?:() => void;
}>) {
    return useQuery({
        ...rest,
        queryKey : [ "Financeiro", "Conta", "Movimentacao", "BuscarPorIdentificador", codigo ],
        queryFn : () => Axios.get<Movimentacao|null>(`financeiro/conta/movimentacao/${codigo}`).finally(onFinish),
        select(data) {
            return data.data ?? null;
        },
        staleTime : 1000 * 60 * 3
    });
}